/**
 * SelfConsumptionIndicator - Alert component showing self-consumption quality.
 *
 * Thresholds:
 *   < 10%  → red (very low — most energy goes to grid)
 *   10–20% → orange (low — room for improvement)
 *   ≥ 20%  → nothing rendered (acceptable/good)
 *
 * Always shows the "lost energy" statistic (Q5: always).
 */

import { Alert, Progress, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { ProsumerSelfConsumption } from '../../types';

export interface SelfConsumptionIndicatorProps {
  selfConsumption: ProsumerSelfConsumption;
}

export function SelfConsumptionIndicator({ selfConsumption }: SelfConsumptionIndicatorProps) {
  if (!selfConsumption) return null;
  const { effectiveRatio, totalProductionKwh, totalSelfConsumptionKwh } = selfConsumption;
  const ratioPercent = Math.round(effectiveRatio * 100);
  const lostKwh = Math.max(0, Math.round(totalProductionKwh - totalSelfConsumptionKwh));

  if (!Number.isFinite(effectiveRatio) || effectiveRatio >= 0.20) return null;

  const isVeryLow = effectiveRatio < 0.10;
  const color = isVeryLow ? 'red' : 'orange';

  const title = isVeryLow
    ? `Niska autokonsumpcja: ${ratioPercent}%`
    : `Umiarkowana autokonsumpcja: ${ratioPercent}%`;

  const message = isVeryLow
    ? 'Większość wyprodukowanej energii trafia do sieci. Rozważ magazyn energii lub zmianę profilu zużycia.'
    : 'Zwiększenie zużycia energii w godzinach produkcji PV (10:00–16:00) może znacząco poprawić opłacalność instalacji.';

  return (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      color={color}
      variant="light"
      data-testid="self-consumption-indicator"
    >
      <Stack gap="xs">
        <Text size="sm" fw={500} data-testid="self-consumption-title">
          {title}
        </Text>
        <Progress
          value={ratioPercent}
          color={color}
          size="sm"
          aria-label={`Autokonsumpcja: ${ratioPercent}%`}
          data-testid="self-consumption-progress"
        />
        <Text size="xs">{message}</Text>
        <Text size="xs" c="dimmed" data-testid="lost-value-message">
          Energia nieodebrana: ok.{' '}
          <Text span fw={500} data-testid="lost-kwh">
            {lostKwh.toLocaleString('pl-PL')} kWh
          </Text>{' '}
          rocznie trafia do sieci — zwracane jest tylko 20–30% jej wartości jako depozyt RCE.
        </Text>
      </Stack>
    </Alert>
  );
}
